import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CalendarRange, ChevronDown, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function FYSwitcher() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState(null);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";

  function load() {
    api.get("/financial-years/active").then(r => setActive(r.data)).catch(() => {});
    api.get("/financial-years").then(r => setList(r.data || [])).catch(() => {});
  }

  useEffect(() => { load(); }, []);

  async function switchTo(code) {
    if (!isAdmin) {
      toast.error("Admin only — only admin can switch financial year");
      return;
    }
    try {
      await api.post(`/financial-years/switch/${code}`);
      toast.success(`Switched to FY ${code}`);
      setOpen(false);
      load();
      // Hard reload so the active FY context is reflected everywhere
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to switch");
    }
  }

  if (!active) return null;

  return (
    <div className="relative" data-testid="fy-switcher">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3.5 py-2 bg-gradient-to-br from-emerald-700 to-emerald-900 text-white border border-amber-300/40 hover:border-amber-300 shadow-sm text-xs font-bold tracking-wider transition-colors"
        data-testid="fy-switcher-btn"
      >
        <CalendarRange size={14} className="text-amber-300" />
        <span className="text-amber-200 uppercase">Active FY:</span>
        <span className="text-white font-black">{active.code}</span>
        {active.status === "Closed" && (
          <span className="text-[9px] bg-red-200 text-red-900 px-1.5 py-0.5 uppercase tracking-wider">Closed</span>
        )}
        <ChevronDown size={12} className="text-amber-300" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-slate-200 shadow-lg z-[70]">
            <div className="px-4 py-2 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              Switch Financial Year
            </div>
            <div className="max-h-72 overflow-y-auto">
              {list.length === 0 ? (
                <div className="p-4 text-xs text-slate-500">No FYs yet</div>
              ) : (
                list.map(fy => (
                  <button
                    key={fy.code}
                    onClick={() => switchTo(fy.code)}
                    className="w-full px-4 py-2.5 text-left hover:bg-slate-50 flex items-center justify-between"
                    data-testid={`fy-option-${fy.code}`}
                  >
                    <div>
                      <div className="text-sm font-bold text-slate-900">FY {fy.code}</div>
                      <div className="text-[10px] text-slate-500">{fy.start_date} → {fy.end_date}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {fy.status === "Closed" && (
                        <span className="text-[9px] bg-red-100 text-red-700 px-1.5 py-0.5 uppercase">Closed</span>
                      )}
                      {fy.is_active && <Check size={14} className="text-[#F97316]" />}
                    </div>
                  </button>
                ))
              )}
            </div>
            <button
              onClick={() => { setOpen(false); navigate("/settings/financial-year"); }}
              className="w-full px-4 py-2.5 border-t border-slate-100 text-[11px] uppercase tracking-wider font-bold text-[#F97316] hover:bg-orange-50"
              data-testid="fy-manage-btn"
            >
              Manage Financial Years →
            </button>
          </div>
        </>
      )}
    </div>
  );
}
